class SegmentedControl {
	readonly #container: HTMLElement;

	constructor(
		private readonly title: string,
		private readonly elements: NodeListOf<HTMLElement>,
		private readonly onChange: (selectedId: string) => void = () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
	) {
		this.#container = this.#createSegmentedControl();
	}

	/**
	 * Render the segmented control before a given element.
	 * @param beforeElement The element before which the control will be rendered.
	 */
	public render(beforeElement: HTMLElement): void {
		beforeElement.before(this.#container);
	}

	/**
	 * Create the segmented control container and its child elements.
	 * @returns The container element.
	 */
	#createSegmentedControl(): HTMLElement {
		const segmentedControl = document.createElement('nav');
		segmentedControl.classList.add('segmented-control');
		segmentedControl.setAttribute('role', 'tablist');
		segmentedControl.setAttribute('aria-label', this.title);

		for (const [index, element] of this.elements.entries()) {
			const isFirst = index === 0;

			// Initialize target element
			this.#initializeTargetElement(element, isFirst);

			// Create control
			const control = this.#createControl(element, isFirst);
			segmentedControl.append(control);
		}

		return segmentedControl;
	}

	/**
	 * Initialize the target element with ARIA attributes and visibility.
	 * @param element The target element.
	 * @param isVisible Whether the element should be visible initially.
	 */
	#initializeTargetElement(element: HTMLElement, isVisible: boolean): void {
		element.hidden = !isVisible;
		element.setAttribute('role', 'tabpanel');
		element.setAttribute('aria-labelledby', `${element.id}-control`);
	}

	/**
	 * Create a tab button for the segmented control.
	 * @param element The associated element.
	 * @param isSelected Whether the button should be selected initially.
	 * @returns The button element.
	 */
	#createControl(
		element: HTMLElement,
		isSelected: boolean,
	): HTMLButtonElement {
		const control = document.createElement('button');
		control.type = 'button';
		control.id = `${element.id}-control`;
		control.tabIndex = isSelected ? 0 : -1;
		control.setAttribute('role', 'tab');
		control.setAttribute('aria-controls', element.id);
		control.setAttribute('aria-selected', isSelected ? 'true' : 'false');
		control.textContent = element.getAttribute('aria-label') ?? element.id;
		control.addEventListener('click', () => {
			this.#activate(element.id);
		});
		control.addEventListener('keyup', (event: KeyboardEvent) => {
			this.#handleKey(event);
		});

		return control;
	}

	/**
	 * Activate an element with a given ID.
	 * @param selectedId The ID of the selected control.
	 */
	#activate(selectedId: string): void {
		for (const element of this.elements) {
			const isSelected = element.id === selectedId;

			// Update target element visibility
			element.hidden = !isSelected;

			// Update control ARIA attributes
			const control = document.querySelector<HTMLElement>(
				`#${element.id}-control`,
			);
			if (control) {
				control.tabIndex = isSelected ? 0 : -1;
				control.setAttribute(
					'aria-selected',
					isSelected ? 'true' : 'false',
				);
				if (isSelected) {
					control.focus();
				}
			}
		}

		// Trigger the onChange callback
		this.onChange(selectedId);
	}

	/**
	 * Handle key events for keyboard navigation.
	 * @param event The keyboard event.
	 */
	#handleKey(event: KeyboardEvent): void {
		const currentElement = event.currentTarget as HTMLButtonElement;
		let nextControl: HTMLButtonElement | null = null;

		if (
			event.key === 'ArrowLeft' ||
			event.key === 'ArrowUp' ||
			event.key === 'End'
		) {
			event.preventDefault();
			nextControl = (currentElement.previousElementSibling ??
				currentElement.nextElementSibling) as HTMLButtonElement | null;
		}

		if (
			event.key === 'ArrowRight' ||
			event.key === 'ArrowDown' ||
			event.key === 'End'
		) {
			event.preventDefault();
			nextControl = (currentElement.nextElementSibling ??
				currentElement.previousElementSibling) as HTMLButtonElement | null;
		}

		if (nextControl) {
			const nextId = nextControl.id.replace('-control', '');
			this.#activate(nextId);
		}
	}
}

export default SegmentedControl;
